import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTenant } from '../../contexts/TenantContext';

export default function AuthCallbackPage() {
  const { addDebugLog } = useTenant();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: Only run once
    if (hasProcessed.current) {
      console.log('[Auth Callback] ⚠️ Already processed - skipping');
      return;
    }

    const handleAuthCallback = async () => {
      try {
        hasProcessed.current = true;
        
        console.log('[Auth Callback] 🎯 Processing authentication callback...');
        addDebugLog('info', 'auth', '[Auth Callback] 🎯 Processing authentication callback...');
        console.log('[Auth Callback] 📍 Current URL:', window.location.href);
        console.log('[Auth Callback] 📍 Hash:', window.location.hash);

        // Extract tokens from URL hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        console.log('[Auth Callback] 📋 Hash parameters:', {
          has_access_token: !!accessToken,
          has_refresh_token: !!refreshToken,
          error,
          errorDescription,
          access_token_length: accessToken?.length || 0
        });

        // Check for errors
        if (error) {
          console.error('[Auth Callback] ❌ Error in URL:', error, errorDescription);
          throw new Error(errorDescription || error);
        }

        if (!accessToken) {
          console.error('[Auth Callback] ❌ No access token found');
          throw new Error('No access token found. Please try logging in again.');
        }

        console.log('[Auth Callback] ✅ Access token found, length:', accessToken.length);

        // Set session
        console.log('[Auth Callback] 🔑 Setting session...');
        addDebugLog('info', 'auth', '[Auth Callback] 🔑 Setting session...');
        
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });

        if (sessionError) {
          console.error('[Auth Callback] ❌ Session error:', sessionError);
          addDebugLog('error', 'auth', '[Auth Callback] ❌ Session error', sessionError);
          throw sessionError;
        }

        if (!sessionData.session) {
          console.error('[Auth Callback] ❌ No session created');
          throw new Error('Failed to create session. Please try again.');
        }

        console.log('[Auth Callback] ✅ Session created successfully');
        addDebugLog('success', 'auth', '[Auth Callback] ✅ Session created', {
          user_id: sessionData.user?.id,
          email: sessionData.user?.email
        });

        // Clean URL hash
        console.log('[Auth Callback] 🧹 Cleaning URL hash...');
        window.history.replaceState(null, '', window.location.pathname);

        // Show success
        setStatus('success');
        addDebugLog('success', 'auth', '[Auth Callback] 🎉 Authentication complete!');

        // Redirect to dashboard without username parameter
        console.log('[Auth Callback] 🚀 Redirecting to dashboard...');
        addDebugLog('success', 'auth', '[Auth Callback] 🚀 Redirecting to /dashboard');
        
        // Small delay to show success message
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);

      } catch (err: any) {
        console.error('[Auth Callback] ❌ Error:', err);
        addDebugLog('error', 'auth', '[Auth Callback] ❌ Error', {
          message: err.message,
          stack: err.stack
        });
        setStatus('error');
        setErrorMessage(err.message || 'Authentication failed. Please try again.');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [addDebugLog, navigate]);

  // Processing State
  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Authenticating...</h1>
              <p className="text-gray-600 mb-6">Please wait while we verify your login</p>
              
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-3xl text-teal-600"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Login Successful!</h1>
              <p className="text-gray-600 mb-6">Redirecting to dashboard...</p>
              
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-600"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Failed</h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            
            <p className="text-sm text-gray-500">Redirecting to login page...</p>
          </div>
        </div>
      </div>
    </div>
  );
}
